import { useUpdateProfile } from "@features/profile/mutations/useUpdateProfile";
import { BIO_TEXT_MAX, type BioContent, flattenBio, GenderEnumSchema, resolveBioContent } from "@fitties/shared";
import { Field, Label, Switch } from "@headlessui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { PhotoUploadField } from "@modules/photoUpload";
import { BioPicker } from "@shared/components/BioPicker";
import DesiredGendersField from "@shared/components/DesiredGendersField";
import clsx from "clsx";
import { useId, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

const profileEditSchema = z.object({
  desired_genders: z.array(GenderEnumSchema).min(1, "Please select at least one option"),
  includeInWeeklyPool: z.boolean(),
});

type ProfileEditFormData = z.infer<typeof profileEditSchema>;

interface ProfileEditFormProps {
  bio: string;
  bioContent?: BioContent;
  desiredGenders: ("female" | "male" | "nonbinary")[];
  photoUrl?: string;
  /** Opted in by default when omitted */
  includeInWeeklyPool?: boolean;
  onSuccess?: () => void;
  /** Optional ref for the form element so parent can trigger requestSubmit (e.g. header save button) */
  formRef?: React.MutableRefObject<HTMLFormElement | null>;
}

/**
 * Profile edit form component for editing user profile information.
 *
 * @example
 * ```tsx
 * <ProfileEditForm
 *   bio="Love fitness"
 *   desiredGenders={["female"]}
 *   photoUrl="https://example.com/photo.jpg"
 *   onSuccess={() => setEditMode(false)}
 * />
 * ```
 */
export default function ProfileEditForm({
  bio,
  bioContent: initialBioContent,
  desiredGenders,
  photoUrl,
  includeInWeeklyPool = true,
  onSuccess,
  formRef,
}: ProfileEditFormProps) {
  const { mutate: updateProfile, error: mutationError } = useUpdateProfile();
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState<string | null>(null);
  const [bioContent, setBioContent] = useState<BioContent | null>(() =>
    resolveBioContent({ bio, bioContent: initialBioContent ?? null })
  );
  const [bioError, setBioError] = useState<string | undefined>();
  const sectionTitleId = useId();
  const interestedInId = useId();

  const headingMono = "font-mono text-xl font-bold text-gray-700 mb-2";

  const methods = useForm<ProfileEditFormData>({
    resolver: zodResolver(profileEditSchema),
    defaultValues: {
      desired_genders: desiredGenders,
      includeInWeeklyPool,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const weeklyPoolEnabled = watch("includeInWeeklyPool") ?? true;

  const handleFormSubmit = (data: ProfileEditFormData) => {
    if (!bioContent) {
      setBioError("Bio is required");
      return;
    }
    if (flattenBio(bioContent).length > BIO_TEXT_MAX) {
      setBioError("Bio must be 150 characters or less");
      return;
    }
    setBioError(undefined);
    const updates: Parameters<typeof updateProfile>[0] = {
      ...data,
      bio: flattenBio(bioContent),
      bioContent,
    };
    if (pendingPhotoUrl !== null) {
      updates.photo_url = pendingPhotoUrl;
    }
    updateProfile(updates, {
      onSuccess: () => {
        onSuccess?.();
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        ref={(el) => {
          if (formRef) {
            formRef.current = el;
          }
        }}
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-6"
        noValidate
        aria-labelledby={sectionTitleId}
        data-testid="profile-edit-form"
      >
        {/* Server Error Message */}
        {mutationError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 text-sm">{mutationError.message}</p>
          </div>
        )}

        {/* Photo upload (saved when user confirms via header save) */}
        <div>
          {/* <span className={`block ${headingMono}`}>Profile Photo</span> */}
          <PhotoUploadField
            {...((pendingPhotoUrl ?? photoUrl) ? { currentPhotoUrl: pendingPhotoUrl ?? photoUrl ?? "" } : {})}
            onUploadComplete={(url) => setPendingPhotoUrl(url)}
          />
        </div>

        <div>
          <span className={`block ${headingMono}`}>Bio</span>
          <BioPicker
            value={bioContent}
            onChange={(next) => {
              setBioContent(next);
              if (next) {
                setBioError(undefined);
              }
            }}
            charCountTestId="profile-edit-bio-char-count"
            {...(bioError ? { error: bioError } : {})}
          />
        </div>

        {/* Interested In */}
        <label htmlFor={interestedInId} className={`block ${headingMono}`}>
          Interested In
        </label>
        <DesiredGendersField
          value={watch("desired_genders") ?? []}
          onChange={(v) => setValue("desired_genders", v)}
          {...(errors.desired_genders?.message != null ? { error: errors.desired_genders.message } : {})}
        />

        <div>
          <span className={`block ${headingMono}`}>Discovery</span>
          <Field className="flex min-w-0 flex-wrap items-center gap-2" data-testid="weekly-pool-toggle-row">
            <Switch
              checked={weeklyPoolEnabled}
              onChange={(checked) => setValue("includeInWeeklyPool", checked, { shouldDirty: true })}
              className={clsx(
                "relative inline-flex h-8 w-20 shrink-0 items-center rounded-lg transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2",
                weeklyPoolEnabled ? "bg-pink-600" : "bg-lavender-900"
              )}
              data-testid="weekly-pool-toggle"
            >
              <span
                className={clsx(
                  "inline-block h-6 w-6 rounded-md bg-white transform transition-transform",
                  weeklyPoolEnabled ? "translate-x-13" : "translate-x-1"
                )}
              />
            </Switch>
            <Label className="min-w-0 flex-1 break-words text-left text-md font-medium text-black">
              Include me in this week's pool
            </Label>
          </Field>
          <p className="mt-2 text-sm text-gray-500" data-testid="weekly-pool-toggle-help">
            When off, you only appear on the night you check in.
          </p>
        </div>
      </form>
    </FormProvider>
  );
}
